import Customer from "./customer";
import ProformaItem from "./proformaItem";
import EconActivity from "./econActivity";
import store from "../store";
import Big from "big.js";
import { computeTaxTotals, baseAtRate } from "./taxCatalog";

// Cota del campo de observaciones. Espeja `Proforma.observations` del backend
// (backend/models.py): de ese max_length dependen el XML que se envia al SRI
// y el RIDE, que recortan el texto si se pasa.
export const OBSERVATIONS_MAX_LENGTH = 400;

const Proforma = class {
    constructor(object) {
        if (object === undefined) {
            this.id = null;
            this.saleId = null;
            this.serial = null;
            this.econActivity = null;
            this.customer = new Customer();
            this.items = [];
            this.createdAt = null;
            this.expiresAt = null;
            this.validDays = 15;
            this.total = new Big('0.0');
            this.discount = new Big('0.0');
            this.sub0 = new Big('0.0');
            this.sub12 = new Big('0.0');
            this.tax = new Big('0.0');
            // Desglose por tarifa (fuente de sub5 / subGeneral en la caja de totales).
            this.taxBuckets = [];
            this.iceValue = new Big('0.0');
            this.taxPercentage = store.getters.business.taxPercentage;
            this.paymentMethod = {name: 'Efectivo', code: 'CASH'};
            this.observations = null;
            this.status = ""
        } else {
            this.id = object.id;
            this.saleId = object.saleId || null;
            this.serial = object.serial;
            this.econActivity = new EconActivity(object.activity);
            this.customer = new Customer(object.customer);
            this.createdAt = new Date(Date.parse(object.createdAt));
            this.validDays = object.validDays;
            this.total = (object.total !== undefined) ? new Big(object.total) : new Big('0.0');
            this.discount = (object.discount !== undefined) ? new Big(object.discount) : new Big('0.0');
            this.sub0 = (object.base0 !== undefined) ? new Big(object.base0) : new Big('0.0');
            this.sub12 = (object.base12 !== undefined) ? new Big(object.base12) : new Big('0.0');
            this.tax = (object.tax !== undefined) ? new Big(object.tax) : new Big('0.0');
            this.taxBuckets = Array.isArray(object.taxLines) ? object.taxLines : [];
            this.iceValue = (object.iceValue !== undefined && object.iceValue !== null) ? new Big(object.iceValue) : new Big('0.0');
            this.taxPercentage = object.taxPercentage || store.getters.business.taxPercentage;
            this.paymentMethod = object.paymentMethod;
            this.observations = object.observations;
            this.status = object.status;

            // Parse expires at
            if (object.expiresAt !== undefined) {
                this.expiresAt = new Date(Date.parse(object.expiresAt));
            } else { this.expiresAt = null }

            // Parse items if exists
            if (object.items !== undefined && object.items !== null && object.items.length > 0) { 
                this.items = object.items.map(item => new ProformaItem(item)); 
            }
        }
    }
    get serialNumber() {
        return this.serial.toString().padStart(9, '0');
    }
    get createdAtFormatted() {
        var options = { year: 'numeric', month: 'numeric', day: 'numeric'};
        return this.createdAt.toLocaleDateString('es-EC', options) + " - " + this.createdAt.toLocaleTimeString('en-US');
    }
    get expiresAtDateFormatted() {
        if (this.expiresAt !== null) {
            var options = { year: 'numeric', month: 'numeric', day: 'numeric'};
            return this.expiresAt.toLocaleDateString('es-EC', options);
        }
        return null;
    }
    get state() {
        if (this.status == "GENERATED") { return "GENERADA" }
        else if (this.status == "EXPIRED") { return "EXPIRADA" }
        else { return "FACTURADA" }
    }
    get stateBadgeColor() {
        if (this.status == "GENERATED") { return "blue" }
        else if (this.status == "EXPIRED") { return "red" }
        else { return "green" }
    }
    // «Crear Venta» solo con una proforma guardada, sin venta y vigente. La vencida
    // es la que la lista pinta EXPIRADA, y el backend también la rechaza.
    get canCreateSale() {
        return this.id !== null && this.saleId === null && this.status !== "EXPIRED";
    }
    get paymentMethodReadable() {
        if (this.paymentMethod === "CASH") { return "Efectivo"}
        if (this.paymentMethod === "TRANSFER") { return "Transferencia"}
        if (this.paymentMethod === "DEBIT_CARD") { return "Tarj. Debito"}
        if (this.paymentMethod === "CREDIT_CARD") { return "Tarj. Credito"}
        if (this.paymentMethod === "MIXED") { return "Mixto"}
        else { return "Cheque" }
    }
    get paymentMethodBadgeColor() {
        if (this.paymentMethod === "CASH") { return "green"}
        if (this.paymentMethod === "TRANSFER") { return "blue"}
        else { return "gray" }
    }
    updateItems(item, append=true) {
        if (append) { this.items.unshift(item) }
        else {
            let index = this.getItemIndex(item);
            if (index === -1) { this.items.unshift(item) } 
            else { this.items[index] = item }
        }
        this.updateValues();
     }
    deleteItem(item) {
        let index = this.getItemIndex(item);
        this.items.splice(index, 1);
        if (this.items.length == 0) { this.items = [] }
        this.updateValues();
    }
    getItemIndex(item) {
        var index = -1;
        if (item.uid === null || this.items === null || this.items === undefined) { return -1; }
        for (var i=0; i<this.items.length; i++) {
            if (this.items[i].uid === item.uid) { return i; }
        }
        return index;
    }

    // Desglose por tarifa para la caja de totales. Se muestra SIEMPRE una fila
    // por tarifa (0% / 5% / general) aunque esté en cero: es el formato estándar
    // del SRI y el mismo que emite el RIDE. Agrupar 5% y 15% en un solo
    // "subtotal gravado" oculta información que el comprobante sí detalla.
    get sub5() {
        return baseAtRate(this.taxBuckets, '5');
    }

    // Todo lo gravado que NO es 5%. Se calcula por resta para que ninguna base
    // desaparezca de la vista (p.ej. un documento histórico al 12% o al 14%).
    // Etiqueta de la fila de tarifa general. Usa la tarifa del DOCUMENTO (que en
    // un comprobante histórico puede ser 12% o 14%, no la vigente) y cae a la del
    // negocio si viniera vacía, para no mostrar nunca un "Subtotal %" sin número.
    get subGeneralLabel() {
        const rate = this.taxPercentage || store.getters.business.taxPercentage || '15';
        return 'Subtotal ' + rate + '%';
    }

    get subGeneral() {
        return this.sub12.minus(this.sub5);
    }

    updateValues() {
        var discount_ = new Big('0.0');
        var taxLines = [];
        for (var i=0; i<this.items.length; i++) {
            var base = this.items[i].amount.times(this.items[i].unitPrice).times(this.items[i].discount.times(-1).plus(1));
            // ICE: si el item trae su producto, se recalcula para que no quede
            // desactualizado al cambiar la cantidad; si no (item ya persistido),
            // se usa el monto congelado que devolvió el backend.
            var prod = this.items[i].productObj;
            var itemIce = (prod && prod.hasIce && prod.estimateIce)
                ? prod.estimateIce(this.items[i].amount, base)
                : this.items[i].iceValue;
            taxLines.push({ ivaCode: this.items[i].ivaCode, base: base,
                            iceValue: itemIce, iceCode: this.items[i].iceCode });
            discount_ = discount_.plus(this.items[i].amount.times(this.items[i].unitPrice).times(this.items[i].discount));
        }
        // El IVA se agrega POR TARIFA (un documento puede mezclar 5% y 15%), no
        // aplicando una sola tarifa del negocio a toda la base gravada.
        // computeTaxTotals es espejo del backend: redondea cada tarifa a 2
        // decimales y de ahí deriva sub0/sub12/tax, así el invariante
        // sub0 + sub12 + tax == total se cumple y coincide al centavo con la BD.
        var totals = computeTaxTotals(taxLines);
        this.taxBuckets = totals.buckets;
        this.sub0 = totals.sub0;
        this.sub12 = totals.sub12;
        this.tax = totals.tax;
        this.iceValue = totals.iceTotal;
        this.discount = discount_.round(2);
        this.total = this.sub0.plus(this.sub12).plus(this.iceValue).plus(this.tax).round(2);
    }
}

export default Proforma